// ─── La misura del LISTINO: dentro una casella c'è un prezzo ─────────────────
//
// Era dentro `Foglio.tsx` finché il foglio serviva solo i prezzi. Adesso che ne
// serve due, il foglio non sa più cosa porta e questo file sì.
//
// ⛔ **Vuoto non è zero, e la separazione comincia da qui**: una casella vuota
// non ha riga nel database e si legge «–»; lo zero si scrive apposta e si legge
// «gratis». Chi le confondesse segnerebbe partite gratis che nessuno rivede.

import type { Misura } from '../foglio/misura.js';
import { euro } from '../../routes/impostazioni/comune.js';

/** «12,50» → 1250. `null` su ciò che non è un importo: mai un `NaN` in giro. */
export function centesimi(testo: string): number | null {
  const pulito = testo.trim().replace(/\s/g, '').replace('€', '').replace(',', '.');
  if (!/^\d+(\.\d{1,2})?$/.test(pulito)) return null;
  return Math.round(Number(pulito) * 100);
}

/** «1300» → «13» · «1250» → «12,50». Il simbolo sta nella legenda, non in ogni casella. */
function corto(cents: number): string {
  return cents % 100 === 0 ? String(cents / 100) : (cents / 100).toFixed(2).replace('.', ',');
}

/**
 * Il prezzo come misura del foglio.
 *
 * ⚠️ `perPlayer` entra solo nel testo che si legge passando sopra a una
 * casella: è una proprietà della TABELLA, non del prezzo, e il foglio non deve
 * conoscerla. Per questo la misura si costruisce per ogni tabella invece di
 * essere una costante.
 */
export function prezzo(perPlayer: boolean): Misura {
  return {
    leggi: centesimi,
    breve: (v) => (v === 0 ? 'gratis' : corto(v)),
    battuto: corto,
    /*
     * ⚠️ Lo zero sta FUORI dalla scala dei colori: ha già il suo tratteggio, e
     * tenerlo dentro schiaccerebbe tutti i prezzi veri nella metà alta — cioè
     * renderebbe simili proprio le bande che la griglia esiste per distinguere.
     */
    tinta(v, tutte) {
      if (v === 0) return undefined;
      const prezzi = [...new Set(tutte.filter((x) => x > 0))].sort((a, b) => a - b);
      if (prezzi.length <= 1) return 'color-mix(in srgb, var(--verde) 30%, transparent)';
      const posto = prezzi.indexOf(v) / (prezzi.length - 1);
      return `color-mix(in srgb, var(--verde) ${Math.round(14 + posto * 44)}%, transparent)`;
    },
    classe: (v) => (v === 0 ? 'gratis' : ''),
    dice(v, giorno, ora) {
      if (v === undefined) return `${giorno} ${ora}: nessun prezzo`;
      if (v === 0) return `${giorno} ${ora}: gratis`;
      return `${giorno} ${ora}: ${euro(v)} ${perPlayer ? 'a testa' : 'il campo'}`;
    },
    niente: '–',
    segnaposto: '€',
    etichettaBarra: 'Prezzo da scrivere nelle caselle scelte',
    etichettaGriglia: 'Le caselle del listino',
    modo: 'decimal',
    comeSiChiama: 'Prezzo',
  };
}

/**
 * 💶 La misura del listino dei maestri: la casella è il prezzo di **un'ora** di
 * campo (voce 19, tappa 19a), e il titolo della casella lo dice così — «8,00 €
 * l'ora», non «il campo», che accanto a una cifra oraria si leggerebbe come il
 * costo di tutta la lezione.
 */
export function prezzoDelCampo(): Misura {
  const base = prezzo(false);
  return {
    ...base,
    dice(v, giorno, ora) {
      if (v === undefined) return `${giorno} ${ora}: nessun prezzo`;
      if (v === 0) return `${giorno} ${ora}: gratis`;
      return `${giorno} ${ora}: ${euro(v)} l’ora`;
    },
  };
}
